import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('bkw_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // SuperAdmin "Manage Tenant" impersonation — attach header to all requests when active
  const acting = sessionStorage.getItem('bkw_acting_tenant_id');
  if (acting) config.headers['X-Acting-Tenant-Id'] = acting;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem('bkw_token');
      sessionStorage.removeItem('bkw_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
};

export const workspaceAPI = {
  list: () => api.get('/workspaces'),
  create: (data) => api.post('/workspaces', data),
  get: (id) => api.get(`/workspaces/${id}`),
  update: (id, data) => api.put(`/workspaces/${id}`, data),
  delete: (id) => api.delete(`/workspaces/${id}`),
  getTags: (id) => api.get(`/workspaces/${id}/tags`),
  addTag: (id, tag) => api.post(`/workspaces/${id}/tags`, { tag }),
  removeTag: (id, tag) => api.delete(`/workspaces/${id}/tags/${encodeURIComponent(tag)}`),
  publishAsAgent: (id, data) => api.post(`/workspaces/${id}/publish-agent`, data),
  listPublishedAgents: () => api.get('/published-agents'),
  unpublishAgent: (agentId) => api.delete(`/published-agents/${agentId}`),
  getIntelligence: (id) => api.get(`/workspaces/${id}/intelligence`),
  regenerateIntelligence: (id) => api.post(`/workspaces/${id}/intelligence/regenerate`),
  getMindmapNodeRefs: (id, body) => api.post(`/workspaces/${id}/mindmap/node-references`, body),
};

export const documentAPI = {
  upload: (workspaceId, formData) =>
    api.post(`/workspaces/${workspaceId}/documents/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  list: (workspaceId, category) =>
    api.get(`/workspaces/${workspaceId}/documents`, { params: { category } }),
  get: (id) => api.get(`/documents/${id}`),
  delete: (id) => api.delete(`/documents/${id}`),
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
};

export const sourceAPI = {
  addWeb: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/sources/web`, data),
  addWebBatch: (workspaceId, urls) => api.post(`/workspaces/${workspaceId}/sources/web/batch`, { urls }),
  deleteWeb: (id) => api.delete(`/sources/web/${id}`),
  addText: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/sources/text`, data),
  deleteText: (id) => api.delete(`/sources/text/${id}`),
  getContent: (sourceId) => api.get(`/sources/${sourceId}/content`),
  toggleActive: (sourceId, isActive) => api.put(`/sources/${sourceId}/toggle`, { is_active: isActive }),
};

export const notesAPI = {
  list: (workspaceId) => api.get(`/workspaces/${workspaceId}/notes`),
  create: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/notes`, data),
  delete: (workspaceId, noteId) => api.delete(`/workspaces/${workspaceId}/notes/${noteId}`),
  addToSources: (workspaceId, noteId) => api.post(`/workspaces/${workspaceId}/notes/${noteId}/add-to-sources`),
};

export const chatAPI = {
  createSession: (workspaceId) => api.post(`/workspaces/${workspaceId}/chat/sessions`),
  listSessions: (workspaceId) => api.get(`/workspaces/${workspaceId}/chat/sessions`),
  sendMessage: (sessionId, data) => api.post(`/chat/sessions/${sessionId}/messages`, data),
  getMessages: (sessionId) => api.get(`/chat/sessions/${sessionId}/messages`),
  sendMessageStream: (sessionId, data) => {
    const token = sessionStorage.getItem('bkw_token');
    return fetch(`${API_URL}/chat/sessions/${sessionId}/messages/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
  },
};

export const agentAPI = {
  run: (workspaceId, agentType, data) =>
    api.post(`/workspaces/${workspaceId}/agents/${agentType}`, data),
  listRuns: (workspaceId) => api.get(`/workspaces/${workspaceId}/agents/runs`),
  listAvailable: (workspaceId) => api.get(`/workspaces/${workspaceId}/available-agents`),
  listCustom: (workspaceId) => api.get(`/workspaces/${workspaceId}/custom-agents`),
  createCustom: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/custom-agents`, data),
  deleteCustom: (id) => api.delete(`/custom-agents/${id}`),
  runCustom: (workspaceId, agentId, data) =>
    api.post(`/workspaces/${workspaceId}/custom-agents/${agentId}/run`, data),
};

export const insightAPI = {
  summarize: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/summarize`, data),
  mindmap: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/mindmap`, data),
  sourceSummary: (workspaceId, sourceId) => api.post(`/workspaces/${workspaceId}/sources/${sourceId}/summary`),
};

export const toolsAPI = {
  catalog: () => api.get('/tools/catalog'),
  run: (workspaceId, toolId, data) => api.post(`/workspaces/${workspaceId}/tools/${toolId}/run`, data),
  runs: (workspaceId) => api.get(`/workspaces/${workspaceId}/tools/runs`),
  cachedResults: (workspaceId) => api.get(`/workspaces/${workspaceId}/tools/cached-results`),
  auditTrail: (workspaceId) => api.get(`/workspaces/${workspaceId}/audit-trail`),
  accessGuard: (workspaceId) => api.get(`/workspaces/${workspaceId}/access-info`),
  reportTypes: () => api.get('/tools/report-types'),
};

export const customToolsAPI = {
  list: () => api.get('/user/custom-tools'),
  create: (data) => api.post('/user/custom-tools', data),
  update: (id, data) => api.put(`/user/custom-tools/${id}`, data),
  remove: (id) => api.delete(`/user/custom-tools/${id}`),
  run: (workspaceId, toolId, data) => api.post(`/workspaces/${workspaceId}/custom-tools/${toolId}/run`, data),
};

export const pinnedAPI = {
  get: () => api.get('/user/pinned-tools'),
  set: (tool_ids) => api.post('/user/pinned-tools', { tool_ids }),
};

export const llmConfigAPI = {
  list: () => api.get('/llm-configs'),
  create: (data) => api.post('/llm-configs', data),
  update: (id, data) => api.put(`/llm-configs/${id}`, data),
  remove: (id) => api.delete(`/llm-configs/${id}`),
};

export const systemAPI = {
  errorLog: () => api.get('/system/error-log'),
  usage: () => api.get('/system/usage'),
};

export const settingsAPI = {
  updateLLM: (workspaceId, data) => api.put(`/workspaces/${workspaceId}/llm-settings`, data),
  updateOverridePolicy: (workspaceId, allow) => api.put(`/workspaces/${workspaceId}/llm-policy`, { allow_user_override: allow }),
  updateLanguage: (workspaceId, language) => api.put(`/workspaces/${workspaceId}/language`, { language }),
  getLLMOptions: (workspaceId) => api.get('/llm-options' + (workspaceId ? `?workspace_id=${workspaceId}` : '')),
  getSystemPrompt: (workspaceId) => api.get(`/workspaces/${workspaceId}/system-prompt`),
  updateSystemPrompt: (workspaceId, system_prompt) => api.put(`/workspaces/${workspaceId}/system-prompt`, { system_prompt }),
};

export const shareAPI = {
  list: (workspaceId) => api.get(`/workspaces/${workspaceId}/shares`),
  add: (workspaceId, email, role) => api.post(`/workspaces/${workspaceId}/shares`, { email, role }),
  update: (workspaceId, shareId, role) => api.put(`/workspaces/${workspaceId}/shares/${shareId}`, { role }),
  remove: (workspaceId, shareId) => api.delete(`/workspaces/${workspaceId}/shares/${shareId}`),
};

export const companyAPI = {
  get: () => api.get('/company'),
  update: (data) => api.put('/company', data),
  listUsers: () => api.get('/company/users'),
  changeUserRole: (userId, data) => api.put(`/company/users/${userId}/role`, data),
  removeUser: (userId) => api.delete(`/company/users/${userId}`),
  invite: (data) => api.post('/company/invite', data),
  listInvites: () => api.get('/company/invites'),
  cancelInvite: (id) => api.delete(`/company/invites/${id}`),
  acceptInvite: (token) => api.post('/company/accept-invite', { token }),
  listRoles: () => api.get('/company/roles'),
  createRole: (data) => api.post('/company/roles', data),
  updateRole: (id, data) => api.put(`/company/roles/${id}`, data),
  deleteRole: (id) => api.delete(`/company/roles/${id}`),
  getToolVisibility: () => api.get('/company/tool-visibility'),
  updateToolVisibility: (tool_id, enabled) => api.put('/company/tool-visibility', { tool_id, enabled }),
  getSSO: () => api.get('/company/sso'),
  updateSSO: (data) => api.put('/company/sso', data),
  myPermissions: () => api.get('/company/my-permissions'),
};

// Phase 3: usage tracking & quotas (read-only for users; SuperAdmin can edit limits).
export const aiUsageAPI = {
  myUsage: (days = 30) => api.get(`/me/usage?days=${days}`),
  myQuotaStatus: (workspaceId) => api.get(`/me/quota-status${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),
  workspaceUsage: (workspaceId, days = 30) => api.get(`/workspaces/${workspaceId}/usage?days=${days}`),
  systemUsage: (days = 30, companyId) => api.get(`/superadmin/usage?days=${days}${companyId ? `&company_id=${companyId}` : ''}`),
  listLimits: () => api.get('/superadmin/usage/limits'),
  upsertLimit: (data) => api.put('/superadmin/usage/limits', data),
  deleteLimit: (limitId) => api.delete(`/superadmin/usage/limits/${limitId}`),
};

// Phase 4 + 4.5 + 5: Fallback chains, Smart Routing, Auto-generated chains, Cost rules, Region pinning, Async jobs.
export const routingAPI = {
  getFallback: (workspaceId) => api.get(`/workspaces/${workspaceId}/fallback`),
  setFallback: (workspaceId, data) => api.put(`/workspaces/${workspaceId}/fallback`, data),
  fallbackEvents: (workspaceId, days = 7) => api.get(`/workspaces/${workspaceId}/fallback/events?days=${days}`),
  autoGenerateChain: (workspaceId) => api.post(`/workspaces/${workspaceId}/fallback/auto-generate`),
  getSmartRouting: (workspaceId) => api.get(`/workspaces/${workspaceId}/smart-routing`),
  setSmartRouting: (workspaceId, enabled) => api.put(`/workspaces/${workspaceId}/smart-routing`, { enabled }),
  getCostRules: (workspaceId) => api.get(`/workspaces/${workspaceId}/cost-rules`),
  setCostRules: (workspaceId, data) => api.put(`/workspaces/${workspaceId}/cost-rules`, data),
  getRegion: (workspaceId) => api.get(`/workspaces/${workspaceId}/preferred-region`),
  setRegion: (workspaceId, region) => api.put(`/workspaces/${workspaceId}/preferred-region`, { region }),
};

// Phase 5.2: async AI jobs (OpenAI-compat queue).
export const aiJobsAPI = {
  enqueue: (data) => api.post('/v1/chat/completions/async', data),
  get: (jobId) => api.get(`/v1/jobs/${jobId}`),
  list: (limit = 20) => api.get(`/v1/jobs?limit=${limit}`),
};

// Phase 1: per-user AI model preferences. Resolved at runtime as User → Workspace → System
// when the workspace admin has flipped on `allow_user_override`.
export const myAIPreferencesAPI = {
  get: () => api.get('/me/ai-preferences'),
  update: (data) => api.put('/me/ai-preferences', data),
  clear: () => api.put('/me/ai-preferences', { provider: null, model: null, temperature: null, max_tokens: null }),
};

export const advancedAPI = {
  // Document Comparison
  compare: (workspaceId, data) => api.post(`/advanced/compare/${workspaceId}`, data),
  listComparisons: (workspaceId) => api.get(`/advanced/comparisons/${workspaceId}`),
  // Workflows
  listWorkflows: (workspaceId) => api.get(`/advanced/workflows/${workspaceId}`),
  createWorkflow: (workspaceId, data) => api.post(`/advanced/workflows/${workspaceId}`, data),
  updateWorkflow: (workspaceId, workflowId, data) => api.put(`/advanced/workflows/${workspaceId}/${workflowId}`, data),
  deleteWorkflow: (workspaceId, workflowId) => api.delete(`/advanced/workflows/${workspaceId}/${workflowId}`),
  runWorkflow: (workspaceId, workflowId) => api.post(`/advanced/workflows/${workspaceId}/${workflowId}/run`),
  listWorkflowRuns: (workspaceId, workflowId) => api.get(`/advanced/workflows/${workspaceId}/${workflowId}/runs`),
  // Knowledge Graph
  generateGraph: (workspaceId) => api.post(`/advanced/knowledge-graph/${workspaceId}`),
  getGraph: (workspaceId) => api.get(`/advanced/knowledge-graph/${workspaceId}`),
  // Multi-Language
  translate: (workspaceId, sourceId, data) => api.post(`/advanced/translate/${workspaceId}/${sourceId}`, data),
  detectLanguage: (workspaceId, sourceId) => api.post(`/advanced/detect-language/${workspaceId}/${sourceId}`),
  // Scheduled Agents
  listSchedules: (workspaceId) => api.get(`/advanced/schedules/${workspaceId}`),
  createSchedule: (workspaceId, data) => api.post(`/advanced/schedules/${workspaceId}`, data),
  updateSchedule: (workspaceId, scheduleId, data) => api.put(`/advanced/schedules/${workspaceId}/${scheduleId}`, data),
  deleteSchedule: (workspaceId, scheduleId) => api.delete(`/advanced/schedules/${workspaceId}/${scheduleId}`),
  runScheduleNow: (workspaceId, scheduleId) => api.post(`/advanced/schedules/${workspaceId}/${scheduleId}/run`),
};

export const superadminAPI = {
  getTenants: () => api.get('/superadmin/tenants'),
  getTenantDetail: (id) => api.get(`/superadmin/tenants/${id}/detail`),
  deleteTenant: (id) => api.delete(`/superadmin/tenants/${id}`),
  renameTenant: (id, name) => api.put(`/superadmin/tenants/${id}/rename`, { name }),
  updateTenantDomain: (id, domain) => api.put(`/superadmin/tenants/${id}/domain`, { custom_domain: domain }),
  getUsers: () => api.get('/superadmin/users'),
  getUserDetail: (id) => api.get(`/superadmin/users/${id}/detail`),
  toggleUser: (id, active) => api.put(`/superadmin/users/${id}/toggle`, { active }),
  getDeploymentMode: () => api.get('/superadmin/deployment-mode'),
  setDeploymentMode: (data) => api.put('/superadmin/deployment-mode', data),
  getErrorLogs: () => api.get('/superadmin/error-logs'),
  getAnalytics: () => api.get('/superadmin/analytics'),
  getUsageAnalytics: () => api.get('/superadmin/analytics/usage'),
  getInteractionAnalytics: () => api.get('/superadmin/analytics/interaction'),
  getContentAnalytics: () => api.get('/superadmin/analytics/content'),
  getAIPerformanceAnalytics: () => api.get('/superadmin/analytics/ai-performance'),
  getEnterpriseAnalytics: () => api.get('/superadmin/analytics/enterprise'),
  // Tool Builder
  listSystemTools: () => api.get('/superadmin/system-tools'),
  getToolTemplates: () => api.get('/superadmin/tool-templates'),
  createSystemTool: (data) => api.post('/superadmin/system-tools', data),
  importToolTemplate: (templateId) => api.post('/superadmin/system-tools/import-template', { template_id: templateId }),
  updateSystemTool: (id, data) => api.put(`/superadmin/system-tools/${id}`, data),
  deleteSystemTool: (id) => api.delete(`/superadmin/system-tools/${id}`),
  // AI Agent Builder
  listAIAgents: () => api.get('/superadmin/ai-agents'),
  createAIAgent: (data) => api.post('/superadmin/ai-agents', data),
  updateAIAgent: (id, data) => api.put(`/superadmin/ai-agents/${id}`, data),
  deleteAIAgent: (id) => api.delete(`/superadmin/ai-agents/${id}`),
  getAgentSources: (id) => api.get(`/superadmin/ai-agents/${id}/sources`),
  addAgentTextSource: (id, data) => api.post(`/superadmin/ai-agents/${id}/sources/text`, data),
  uploadAgentSource: (id, formData) => api.post(`/superadmin/ai-agents/${id}/sources/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteAgentSource: (agentId, sourceId) => api.delete(`/superadmin/ai-agents/${agentId}/sources/${sourceId}`),
  getAgentTenants: (id) => api.get(`/superadmin/ai-agents/${id}/tenants`),
  assignAgentTenant: (agentId, companyId) => api.post(`/superadmin/ai-agents/${agentId}/tenants`, { company_id: companyId }),
  unassignAgentTenant: (agentId, companyId) => api.delete(`/superadmin/ai-agents/${agentId}/tenants/${companyId}`),
  getAgentTools: (id) => api.get(`/superadmin/ai-agents/${id}/tools`),
  assignAgentTool: (agentId, toolId, toolSource) => api.post(`/superadmin/ai-agents/${agentId}/tools`, { tool_id: toolId, tool_source: toolSource }),
  unassignAgentTool: (agentId, toolId) => api.delete(`/superadmin/ai-agents/${agentId}/tools/${toolId}`),
  getAvailableTools: () => api.get('/superadmin/available-tools'),
  getGlobalToolConfig: () => api.get('/superadmin/tool-config'),
  updateGlobalToolConfig: (tool_id, blocked) => api.put('/superadmin/tool-config', { tool_id, blocked }),
  getTenantToolConfig: (tenantId) => api.get(`/superadmin/tenants/${tenantId}/tool-config`),
  updateTenantToolConfig: (tenantId, tool_id, enabled) => api.put(`/superadmin/tenants/${tenantId}/tool-config`, { tool_id, enabled }),
  getFeatureConfig: () => api.get('/superadmin/feature-config'),
  updateFeatureConfig: (feature_id, enabled) => api.put('/superadmin/feature-config', { feature_id, enabled }),
  getTenantFeatureConfig: (tenantId) => api.get(`/superadmin/tenants/${tenantId}/feature-config`),
  updateTenantFeatureConfig: (tenantId, feature_id, enabled) => api.put(`/superadmin/tenants/${tenantId}/feature-config`, { feature_id, enabled }),
  // Licensing
  listLicenses: () => api.get('/superadmin/licenses'),
  generateLicense: (data) => api.post('/superadmin/licenses', data),
  revokeLicense: (id) => api.post(`/superadmin/licenses/${id}/revoke`),
  deleteLicense: (id) => api.delete(`/superadmin/licenses/${id}`),
  getLicensePublicKey: () => api.get('/superadmin/licenses/public-key'),
  // External Agents (3rd-party)
  listExternalAgents: () => api.get('/superadmin/external-agents'),
  createExternalAgent: (data) => api.post('/superadmin/external-agents', data),
  updateExternalAgent: (id, data) => api.put(`/superadmin/external-agents/${id}`, data),
  deleteExternalAgent: (id) => api.delete(`/superadmin/external-agents/${id}`),
  testExternalAgent: (id, query) => api.post(`/superadmin/external-agents/${id}/test`, { query }),
  getExternalAgentTenants: (id) => api.get(`/superadmin/external-agents/${id}/tenants`),
  assignExternalAgentTenant: (agentId, companyId) => api.post(`/superadmin/external-agents/${agentId}/tenants`, { company_id: companyId }),
  unassignExternalAgentTenant: (agentId, companyId) => api.delete(`/superadmin/external-agents/${agentId}/tenants/${companyId}`),
  // Vector Backend
  getVectorBackend: () => api.get('/superadmin/vector-backend'),
  updateVectorBackend: (data) => api.put('/superadmin/vector-backend', data),
  testVectorBackend: (backend, config) => api.post('/superadmin/vector-backend/test', { backend, config }),
  saveVectorIndicesNow: () => api.post('/superadmin/vector-backend/save-now'),
  migrateVectorBackend: () => api.post('/superadmin/vector-backend/migrate'),
  // Admin LLMs / AI Models
  listLLMs: () => api.get('/superadmin/llms'),
  createLLM: (data) => api.post('/superadmin/llms', data),
  updateLLM: (id, data) => api.put(`/superadmin/llms/${id}`, data),
  toggleLLM: (id) => api.post(`/superadmin/llms/${id}/toggle`),
  setDefaultLLM: (id) => api.post(`/superadmin/llms/${id}/set-default`),
  deleteLLM: (id) => api.delete(`/superadmin/llms/${id}`),
  getLLMTenants: (id) => api.get(`/superadmin/llms/${id}/tenants`),
  assignLLMTenant: (llmId, companyId) => api.post(`/superadmin/llms/${llmId}/tenants`, { company_id: companyId }),
  unassignLLMTenant: (llmId, companyId) => api.delete(`/superadmin/llms/${llmId}/tenants/${companyId}`),
  listAllTenantsForLLM: () => api.get('/superadmin/tenants-list'),
  clearErrorLogs: () => api.delete('/superadmin/error-logs'),
  getHealthMetrics: () => api.get('/superadmin/health/metrics'),
  listTenantAdmins: (tenantId) => api.get(`/superadmin/tenants/${tenantId}/admins`),
};

// Public (no-auth) endpoints
export const publicAPI = {
  getDeploymentInfo: () => api.get('/deployment/info'),
};

// SSO
export const ssoAPI = {
  initiate: () => api.get('/auth/sso/initiate'),
};

export const externalAgentsAPI = {
  listAvailable: () => api.get('/external-agents/available'),
  run: (agentId, query) => api.post(`/external-agents/${agentId}/run`, { query }),
};

export const licenseAPI = {
  status: () => api.get('/license/status'),
  install: (token) => api.post('/license/install', { token }),
  uninstall: () => api.delete('/license/uninstall'),
};

export const brainyAPI = {
  getConfig: () => api.get('/brainy/config'),
  updateConfig: (data) => api.put('/brainy/config', data),
  listTasks: (status) => api.get('/brainy/tasks', { params: status ? { status } : {} }),
  createTask: (data) => api.post('/brainy/tasks', data),
  updateTask: (id, data) => api.put(`/brainy/tasks/${id}`, data),
  deleteTask: (id) => api.delete(`/brainy/tasks/${id}`),
  listReminders: (status) => api.get('/brainy/reminders', { params: status ? { status } : {} }),
  createReminder: (data) => api.post('/brainy/reminders', data),
  updateReminder: (id, data) => api.put(`/brainy/reminders/${id}`, data),
  deleteReminder: (id) => api.delete(`/brainy/reminders/${id}`),
  getStats: () => api.get('/brainy/stats'),
};

export default api;
